/**
 * Global keyboard helpers. Replaces Phaser's scene.input.keyboard. A single
 * listener is attached once; code subscribes to named keys via on(code, handler).
 */

type Handler = (event: KeyboardEvent) => void;

const downListeners = new Map<string, Set<Handler>>();
const upListeners = new Map<string, Set<Handler>>();
const downKeys = new Set<string>();
let installed = false;

const ensureInstalled = (): void => {
  if (installed) return;
  installed = true;
  window.addEventListener('keydown', (e) => {
    if (e.repeat) return;
    downKeys.add(e.code);
    downListeners.get(e.code)?.forEach((h) => h(e));
  });
  window.addEventListener('keyup', (e) => {
    downKeys.delete(e.code);
    upListeners.get(e.code)?.forEach((h) => h(e));
  });
};

export const onKeyDown = (code: string, handler: Handler): () => void => {
  ensureInstalled();
  let set = downListeners.get(code);
  if (!set) {
    set = new Set();
    downListeners.set(code, set);
  }
  set.add(handler);
  return () => set!.delete(handler);
};

export const onKeyUp = (code: string, handler: Handler): () => void => {
  ensureInstalled();
  let set = upListeners.get(code);
  if (!set) {
    set = new Set();
    upListeners.set(code, set);
  }
  set.add(handler);
  return () => set!.delete(handler);
};

export const isKeyDown = (code: string): boolean => downKeys.has(code);

/** Remove every registered listener for a specific key code (both down + up). */
export const clearKeyListeners = (code: string): void => {
  downListeners.delete(code);
  upListeners.delete(code);
};
